import { EMAIL_RE } from 'utils/validation/email'

import type { AddressDTO, PaymentMethod } from 'models/purchaseFlowRevamp'
import type { NextStepValidationError } from 'models/purchaseFlow/nextStepValidationError'

import { isOrderChargeable } from '../../steps/billing/chargeable'
import type { PfStoreState } from '../store'
import { selectCartTotal } from './cart'

/**
 * The Billing step's gate: a saved invoicing address (one with a backend id), a payment method unless
 * nothing on the order is charged, and well-formed CC emails.
 *
 * The defaults are the ones Billing shows without writing them to the store — the backend-preferred
 * method and a sole saved address. `serializePlacement` falls back to the same defaults, so what opens
 * the gate is what the order carries.
 */
export const selectBillingStepValidFor = (
  defaultPaymentMethod?: PaymentMethod,
  cartKinds: ReadonlyArray<string> = [],
  /**
   * The derived single-saved-address default, supplied by the
   * caller exactly as `defaultPaymentMethod` is. `InvoicingAddressSection`
   * highlights a sole address at RENDER time only (§11.1 bans mount-time
   * seeding), so without this the form looked complete and Proceed stayed dead.
   * Only ever ONE address: there is no BE `isDefault` on `InvoicingAddressDTO`,
   * so choosing among several would be a guess. Threading it here alone would
   * NOT be safe — see the note above; the same default must reach
   * `serializePlacement`, or the gate opens and body assembly then throws.
   */
  defaultInvoicingAddress?: AddressDTO
) => (state: PfStoreState): boolean => {
  const { ccEmails } = state
  const invoicingAddress = state.invoicingAddress ?? defaultInvoicingAddress
  const paymentMethod = state.paymentMethod ?? defaultPaymentMethod

  // The chargeable test mirrors the backend's, so a method is waived only where placement accepts none.
  if (!paymentMethod && isOrderChargeable(selectCartTotal(state).gt(0), cartKinds)) return false

  if (!invoicingAddress) return false
  // An address without an id was started and never saved.
  if (!invoicingAddress.id || invoicingAddress.id.length === 0) return false

  if (ccEmails && ccEmails.length > 0) {
    for (const email of ccEmails) {
      if (!EMAIL_RE.test(email)) return false
    }
  }

  return true
}

/** The Billing gate with no defaults: only an explicit pick counts. */
export const selectBillingStepValid = selectBillingStepValidFor()

/**
 * Why the Billing gate is closed, as `validation_errors` codes; empty when it is open. Mirrors
 * `selectBillingStepValidFor` rule for rule, with the same defaults, so change the two together.
 */
export const selectBillingStepErrorsFor = (
  defaultPaymentMethod?: PaymentMethod,
  cartKinds: ReadonlyArray<string> = [],
  defaultInvoicingAddress?: AddressDTO
) => (state: PfStoreState): NextStepValidationError[] => {
  const errors: NextStepValidationError[] = []
  const invoicingAddress = state.invoicingAddress ?? defaultInvoicingAddress
  const paymentMethod = state.paymentMethod ?? defaultPaymentMethod

  if (!paymentMethod && isOrderChargeable(selectCartTotal(state).gt(0), cartKinds)) errors.push('payment_method_missing')
  if (!invoicingAddress?.id) errors.push('invoicing_address_missing')
  if (state.ccEmails?.some((email) => !EMAIL_RE.test(email))) errors.push('cc_email_invalid')

  return errors
}
